import { cn } from '@dialogueconsulting/sked-ui/lib/utils'
import type { FC } from 'react'
import { useCallback, useMemo } from 'react'

import ConversationHeader from './ConversationHeader'
import ConversationThread from './ConversationThread'
import {
  useConversationExpanded,
  useConversationLoader,
  useConversationReplied,
} from './hooks'
import type { ConversationProps } from './types'
import { formatMessageText } from './utils'

const Conversation: FC<ConversationProps> = (props) => {
  const {
    isLoading,
    messages,
    isReplied: isRepliedDesktop,
    isMobile,
    reviewKey = '',
    postId,
    fetchComment,
    isConversationsEnabled,
    collaborators,
    isReadOnly,
    status,
    isStatusChangesVisible,
    isExternalNotesVisible,
    isPostActivityVisible,
    username,
  } = props

  const { isExpanded, setIsExpanded, getCollapseProps, getToggleProps } =
    useConversationExpanded(status, isMobile)

  const currentPost = messages?.find((item) => item.postId === postId)
  const currentPostMessages = useMemo(
    () => currentPost?.messages || [],
    [currentPost?.messages]
  )

  const filteredMessages = useMemo(() => {
    return currentPostMessages.filter((message) => {
      const { variant, visibility } = message

      const isPostActivityVariant =
        variant === 'postTypeChange' || variant === 'postCreated'
      if (!isPostActivityVisible && isPostActivityVariant) {
        return false
      }

      const isExternalNote = variant === 'note' && visibility === 'EXTERNAL'
      if (!isExternalNotesVisible && isExternalNote) {
        return false
      }

      if (variant === 'statusChange' && !isStatusChangesVisible) {
        return false
      }

      return true
    })
  }, [
    currentPostMessages,
    isExternalNotesVisible,
    isStatusChangesVisible,
    isPostActivityVisible,
  ])

  const hasMessages = filteredMessages.length > 0
  const isReplied = useConversationReplied(
    isRepliedDesktop,
    status,
    currentPostMessages
  )

  useConversationLoader(
    !!isExpanded,
    isLoading,
    reviewKey,
    postId,
    fetchComment
  )

  const formatText = useCallback(
    (text: string, textOnly = false) =>
      formatMessageText(text, collaborators, textOnly),
    [collaborators]
  )

  const handleToggle = useCallback(() => {
    if (!isLoading) {
      setIsExpanded(!isExpanded)
    }
  }, [isLoading, isExpanded, setIsExpanded])

  if (!isMobile && isLoading) return null

  if (filteredMessages.length === 0) return <></>

  return (
    <div
      data-testid="conversation-container"
      className={cn(
        // Desktop: fill the post-action column's remaining height (flex child)
        // so the thread scrolls; full-width so its dividers bleed to the edges.
        isMobile ? 'max-w-full' : 'flex min-h-0 flex-1 flex-col'
      )}
      onClick={handleToggle}
    >
      {isMobile && (
        <ConversationHeader
          isExpanded={!!isExpanded}
          isMobile={isMobile || false}
          isReadOnly={isReadOnly}
          hasMessages={hasMessages}
          isConversationsEnabled={isConversationsEnabled}
          onToggle={handleToggle}
          {...(isMobile ? getToggleProps() : {})}
        />
      )}

      <div
        className={cn(!isMobile && 'flex min-h-0 flex-1 flex-col')}
        {...(isMobile ? { ...getCollapseProps() } : {})}
      >
        <div className={cn(!isMobile && 'flex min-h-0 flex-1 flex-col')}>
          {(hasMessages || isReplied) && isConversationsEnabled && (
            <ConversationThread
              messages={filteredMessages}
              formatText={formatText}
              isReplied={isReplied}
              isMobile={isMobile || false}
              username={username}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default Conversation
